// PEÇA 017 — Títulos a receber (tabela de baixa).
//
// A tabela dos títulos que estão sendo baixados, com o "Pagar" editável por linha, o total e a trava
// do teto. É a MESMA peça nos dois lados da baixa, que são espelhos um do outro:
//   • Contas a Receber: parte dos TÍTULOS (o operador já escolheu) e procura o dinheiro.
//   • Conciliação Bancária: parte do DINHEIRO (o Pix que caiu) e procura os títulos — aí a tabela
//     entra com `selecionavel`, que marca os títulos e aceita desmarcar.
//
// Ela só desenha e calcula: quem baixa é sempre o mesmo endpoint (POST /contas-receber/baixar-lote,
// que amarra o recebimento aos títulos). Nunca duplicar a baixa num segundo lugar — dinheiro tem que
// ter um caminho só.
import { useEffect, useMemo } from 'react';
import { MoneyInput } from '../inputs/MoneyInput';
import { formatMoeda, formatDataSP } from '@fiodecobre/shared';

export interface TituloParaReceber {
  id: number;
  orcamento_numero: string | null;
  origem_legado?: string | null;
  data_vencimento: string;
  valor: string | number;
  vencido?: boolean;
  dias_atraso?: number;
  // Só pro recibo (o papel é nominal e lista o título por emissão/valor original).
  cliente_nome?: string;
  cliente_cpf_cnpj?: string | null;
  valor_original?: string | null;
  data_emissao?: string;
  /** A nota do pedido ([DA-632]) — vem da MESMA rota `/api/contas-receber` que alimenta as duas
   *  portas da baixa, e sai impressa no recibo. O tipo viaja junto: o mesmo numero existe nas
   *  duas series. */
  nota_tipo?: 'nfe' | 'nfce' | null;
  nota_numero?: number | null;
}

export function TitulosParaReceber({
  titulos, valores, onValores, selecionados, onSelecionados, teto, rotuloTeto,
}: {
  titulos: TituloParaReceber[];
  valores: Record<number, number>;
  onValores: (v: Record<number, number>) => void;
  /** Quando vem, a tabela ganha checkbox por linha (a Conciliação escolhe QUAIS títulos o Pix paga). */
  selecionados?: Set<number>;
  onSelecionados?: (s: Set<number>) => void;
  /** Disponível do recebimento casado. O total não pode passar dele — não se quita sem lastro. */
  teto?: number;
  rotuloTeto?: string;
}) {
  const selecionavel = !!selecionados && !!onSelecionados;
  const emJogo = useMemo(
    () => (selecionavel ? titulos.filter((t) => selecionados!.has(t.id)) : titulos),
    [titulos, selecionados, selecionavel],
  );
  const total = useMemo(
    () => +emJogo.reduce((s, t) => s + (valores[t.id] || 0), 0).toFixed(2),
    [emJogo, valores],
  );
  const estourou = teto != null && total > teto + 0.005;

  // Título que entra na seleção nasce com o valor cheio; o que sai zera. Assim o total sempre reflete
  // o que está marcado, sem o operador ter que digitar nada no caso comum (o Pix cobre tudo).
  useEffect(() => {
    if (!selecionavel) return;
    const novo = { ...valores };
    let mudou = false;
    for (const t of titulos) {
      const marcado = selecionados!.has(t.id);
      const atual = novo[t.id] ?? 0;
      if (marcado && atual <= 0) { novo[t.id] = Number(t.valor); mudou = true; }
      if (!marcado && atual > 0) { novo[t.id] = 0; mudou = true; }
    }
    if (mudou) onValores(novo);
  }, [selecionados, titulos, selecionavel]); // eslint-disable-line react-hooks/exhaustive-deps

  function alternar(id: number) {
    const n = new Set(selecionados);
    if (n.has(id)) n.delete(id); else n.add(id);
    onSelecionados!(n);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ maxHeight: '40vh', minHeight: 100, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
        <table className="tabela" style={{ width: '100%' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--panel-bg)' }}>
            <tr>
              {selecionavel && <th style={{ width: 34 }} />}
              <th>Orcamento</th>
              <th>Vencimento</th>
              <th style={{ textAlign: 'right' }}>Valor</th>
              <th style={{ width: 140 }}>Pagar</th>
            </tr>
          </thead>
          <tbody>
            {titulos.map((t) => {
              const marcado = !selecionavel || selecionados!.has(t.id);
              return (
                <tr key={t.id} style={selecionavel && !marcado ? { opacity: 0.5 } : undefined}>
                  {selecionavel && (
                    <td>
                      <input type="checkbox" checked={marcado} onChange={() => alternar(t.id)} />
                    </td>
                  )}
                  <td>{t.orcamento_numero || t.origem_legado || '-'}</td>
                  <td>
                    {formatDataSP(t.data_vencimento)}
                    {t.vencido && (
                      <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#b45309' }}>
                        vencida{t.dias_atraso ? ` (${t.dias_atraso}d)` : ''}
                      </span>
                    )}
                  </td>
                  <td className="cel-num" style={{ textAlign: 'right' }}>{formatMoeda(t.valor)}</td>
                  <td>
                    <MoneyInput
                      valor={valores[t.id] ?? 0}
                      onChangeValor={(v) => onValores({ ...valores, [t.id]: v })}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 600, marginTop: 12 }}>
        <span>Total a receber{selecionavel ? ` (${emJogo.length} de ${titulos.length})` : ''}:</span>
        <span className="cel-num-forte">{formatMoeda(total)}</span>
      </div>

      {estourou && (
        <div style={{ fontSize: 12, color: '#b91c1c', fontWeight: 600, marginTop: 6 }}>
          O total passou do recebimento ({formatMoeda(teto!)}
          {rotuloTeto ? ` — ${rotuloTeto}` : ''}). Desmarque títulos ou reduza os valores — ele só cobre até aí.
        </div>
      )}
    </div>
  );
}

/** Total do que está marcado pra pagar. Mesma conta da tabela — quem chama usa pra travar o Confirmar. */
export function totalTitulos(
  titulos: TituloParaReceber[],
  valores: Record<number, number>,
  selecionados?: Set<number>,
): number {
  const emJogo = selecionados ? titulos.filter((t) => selecionados.has(t.id)) : titulos;
  return +emJogo.reduce((s, t) => s + (valores[t.id] || 0), 0).toFixed(2);
}
